import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, OnInit, signal } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbInputModule,
  NbSelectModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, first, map, Observable, of, switchMap, zip } from 'rxjs';
import { APIService, KPI } from '../../../../@core/api';
import { GROUP_PERMISSION_EDIT_TEAM_MEMBER } from '../../../../@core/api/models';
import { PerimetersService, SaveToastService, ScopeService, UsersService } from '../../../../@core/utils';
import { idMapToArray, toLoadingState } from '../../../../@core/utils/scope.service';
import { BaseFormComponent } from '../../../components/side-forms/base-form/base-form.component';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';

@Component({
  selector: 'app-site-form',
  templateUrl: './site-form.component.html',
  styleUrls: ['../base-form/base-form.component.scss', './site-form.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NgClass,
    NbSpinnerModule,
    NbButtonModule,
    FontAwesomeModule,
    NbInputModule,
    TranslateModule,
    AsyncPipe,
    NbSelectModule,
    NbCheckboxModule,

  ],
})
export class SitesSideFormComponent extends BaseFormComponent implements OnInit {

  @Input() siteId: number = null;

  public title: string;
  public step: number = 1;
  public canLeave: boolean = true;
  // placeholder, next we will implement the creation site permission
  public canAdd: boolean = true;
  // placeholder, next we will implement the modification perimeter permission
  public canModify = signal(true)
  public site: KPI.Perimeter
  // Create mode: single picture staged for upload after site creation
  public selectedPictureFile: File | null = null;
  public picturePreviewUrl: string | null = null;
  // Edit mode: multi-picture management
  public existingPictures: KPI.PerimeterPicture[] = [];
  public coverId: number | null = null;
  private initialCoverId: number | null = null;
  public picturesLoading = false;
  public newPictureFiles: File[] = [];
  public newPicturePreviewUrls: string[] = [];
  public perimeterTypes: KPI.PerimeterType[] = [];
  public currentYear = new Date().getFullYear();

  public clusters$ = this.scopeService.clusters$.pipe(
    idMapToArray(),
    map((cluster) => cluster.sort((a, b) => a.order - b.order)),
    toLoadingState<KPI.TaxonomyObjective[]>(),
  );
  public currentStep: number = 1;
  public allUsers: KPI.User[] = [];
  public selectedUserIds: Set<number> = new Set();
  public userSearchText: string = '';
  public createdSiteId: number = null;
  public currentUserId: number = null;
  public hasEditPermission: boolean = false;
  private initialPerimeters: any[] = [];
  private clustersMap: { [id: number]: string } = {};
  public clustersMap$ = this.scopeService.clusters$.pipe(
    map((clusters) => {
      const map: { [id: number]: string } = {};
      Object.values(clusters).forEach(cluster => {
        map[cluster.id] = cluster.name;
      });
      this.clustersMap = map;
      return map;
    })
  );

  constructor(
    protected nbDialogRef: NbDialogRef<BaseFormComponent>,
    protected dialogService: NbDialogService,
    protected translate: TranslateService,
    private scopeService: ScopeService,
    private perimetersService: PerimetersService,
    private usersService: UsersService,
    private formBuilder: UntypedFormBuilder,
    private api: APIService,
    private saveToast: SaveToastService,
  ) {
    super(nbDialogRef, dialogService, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Backdrop-click-to-close only applies outside the creation wizard (siteId set).
    if (this.siteId) {
      this.initializeBackdropClick();
    }
    this.subscriptions.push(
      of(this.siteId).pipe(
        switchMap((id) => {
          if (id === null) {
            return of(null);
          } else {
            return this.perimetersService.siteByIdAllPerimeters(id);
          }
        })
      ).subscribe((perimeter) => {

        if (this.siteId) {
          this.site = perimeter;
          if (this.site) {
            this.coverId = this.site.cover_image ?? null;
            this.initialCoverId = this.coverId;
            this.loadExistingPictures();
          }
        }

        this.initialValues = {
          name: this.site ? this.site.name : '',
          cluster: this.site ? this.site.cluster : null,
        };

        const level1SubPerimeters = this.site?.sub_perimeters?.filter(p => !p.level_parent) ?? [];
        const initialPerimeters = level1SubPerimeters.length > 0
          ? level1SubPerimeters.map(p => ({
              id: p.id || null,
              name: p.name || '',
              flooring_area: p.flooring_area || null,
              perimeter_type: p.perimeter_type || null,
              creation_year: p.creation_year || null,
            }))
          : [{ id: null, name: '', flooring_area: null, perimeter_type: null, creation_year: null }];

        this.initialPerimeters = initialPerimeters;

        this.form = this.formBuilder.group({
          name: new UntypedFormControl(
            { value: this.initialValues.name, disabled: false },
            [Validators.required, Validators.maxLength(255)],
          ),
          cluster: new UntypedFormControl(
            { value: this.initialValues.cluster, disabled: false },
            this.siteId ? [] : [Validators.required]
          ),
          perimeters: this.formBuilder.array(
            initialPerimeters.map(p => this.createPerimeterFormGroup(p))
          ),
        });

        this.loadPerimeterTypes();

        this.form.get('cluster').valueChanges.subscribe(() => {
          this.loadPerimeterTypes();
        });

        this.subscriptions.push(
          this.clustersMap$.subscribe()
        );
      })
    );
  }

  // Create mode: stage a picture for upload after site creation
  public onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.selectedPictureFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.picturePreviewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  public deletePicture(): void {
    this.selectedPictureFile = null;
    this.picturePreviewUrl = null;
    this.resetFileInput();
  }

  // Edit mode: multi-picture management

  public loadExistingPictures(): void {
    this.picturesLoading = true;
    this.api.structures_perimeter_pictures(this.siteId).subscribe({
      next: (pictures) => {
        this.existingPictures = pictures;
        this.picturesLoading = false;
      },
      error: () => { this.picturesLoading = false; },
    });
  }

  public onNewPictureSelected(event: any): void {
    const file: File = event.target.files[0];
    if (!file) return;
    this.newPictureFiles = [...this.newPictureFiles, file];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.newPicturePreviewUrls = [...this.newPicturePreviewUrls, e.target.result];
    };
    reader.readAsDataURL(file);
    this.resetNewFileInput();
  }

  private uploadNewPicture(): Observable<void> {
    if (!this.newPictureFiles.length) return of(undefined as void);
    const uploads = this.newPictureFiles.map((file) => {
      const formData = new FormData();
      formData.append('picture', file);
      formData.append('perimeter', String(this.siteId));
      return this.api.structures_create_perimeter_picture(formData);
    });
    return zip(uploads).pipe(
      map((pictures) => {
        const wasEmpty = this.existingPictures.length === 0;
        this.existingPictures = [...this.existingPictures, ...pictures];
        if (wasEmpty && pictures.length > 0) {
          this.coverId = pictures[0].id;
          this.initialCoverId = this.coverId;
          const url = pictures[0].picture || null;
          this.api.structures_set_perimeter_cover_image(this.siteId, pictures[0].id).subscribe(() => {
            this.perimetersService.notifyPictureChanged(this.siteId, url);
          });
        } else {
          const cover = this.existingPictures.find(p => p.id === this.coverId);
          this.perimetersService.notifyPictureChanged(this.siteId, cover?.picture || null);
        }
        this.newPictureFiles = [];
        this.newPicturePreviewUrls = [];
      }),
    );
  }

  public cancelNewPicture(index: number): void {
    this.newPictureFiles = this.newPictureFiles.filter((_, i) => i !== index);
    this.newPicturePreviewUrls = this.newPicturePreviewUrls.filter((_, i) => i !== index);
  }

  public deleteExistingPicture(picture: KPI.PerimeterPicture): void {
    this.picturesLoading = true;
    this.api.structures_delete_perimeter_picture(picture.id).subscribe({
      next: () => {
        this.existingPictures = this.existingPictures.filter(p => p.id !== picture.id);
        if (this.coverId === picture.id) {
          this.coverId = this.existingPictures[0]?.id ?? null;
        }
        this.picturesLoading = false;
      },
      error: () => { this.picturesLoading = false; },
    });
  }

  public setAsCover(picture: KPI.PerimeterPicture): void {
    this.coverId = picture.id;
  }

  private saveCoverChange(): Observable<void> {
    if (this.coverId === this.initialCoverId) return of(undefined as void);
    return this.api.structures_set_perimeter_cover_image(this.siteId, this.coverId).pipe(
      map(() => {
        const cover = this.existingPictures.find(p => p.id === this.coverId);
        this.perimetersService.notifyPictureChanged(this.siteId, cover?.picture || null);
      })
    );
  }

  public isCover(picture: KPI.PerimeterPicture): boolean {
    return picture.id === this.coverId;
  }

  public triggerNewPictureInput(): void {
    const fileInput = document.getElementById('new-picture') as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  private resetNewFileInput(): void {
    const fileInput = document.getElementById('new-picture') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  public onSubmit(): void {
    // In edit mode, allow partial saves: validate each building individually in the save loop.
    // In create mode, validate the whole form up-front.
    if (!this.siteId && !this.isFormValid()) {
      return;
    }

    if (this.siteId) {
      if (!this.anyFieldValueChanged()) {
        return;
      }

      this.loading = true;
      this.saveToast.showSaving();

      // Update existing site: only include site-level fields that exist, have changed,
      // and are individually valid. This way an invalid field (e.g. empty name) won't
      // block saves for other valid changes.
      const changes: { property: string, value: any }[] = Object.keys(this.initialValues)
        .filter((key) => {
          const formControl = this.form.get(key);
          return formControl !== null && formControl.valid && this.fieldValueChanged(key);
        })
        .map((key: string) => {
          const formControl = this.form.get(key);
          return {
            property: key,
            value: formControl ? formControl.value : null,
          };
        })
        .filter((change) => change.value !== null); // Remove null values

      const formData = new FormData();
      changes.forEach(change => {
        if (change.hasOwnProperty('property') && change.hasOwnProperty('value')) {
          formData.append(change['property'], change['value']);
        }
      });

      const obs = []
      let hasCreatedPerimeter = false;

      this.getPerimetersFormArray().controls.forEach((control) => {
        if (!control.dirty) return
        const perimeterId = control.value.id;
        const perimeterValues = this.getPerimeterValues(control as UntypedFormGroup);
        if (!perimeterId) {
          hasCreatedPerimeter = true;
          obs.push(
            this.perimetersService.addMonoPerimeter(this.siteId, perimeterValues).pipe(
              map((createdPerimeter: KPI.Perimeter) => ({
                ...perimeterValues,
                ...createdPerimeter,
                id: createdPerimeter.id,
                name: createdPerimeter.name || perimeterValues.name,
                parent: this.siteId,
                cluster: this.site?.cluster,
                is_monosite: true,
                sub_perimeters: [],
              }))
            )
          )
        } else {
          obs.push(this.perimetersService.updateMonoPerimeter(perimeterId, perimeterValues))
        }
      });

      this.perimetersService.updateMultiPerimeter(this.siteId, formData).pipe(
        switchMap(() => {
          if (obs.length) return zip(obs)
          return of([])
        }),
        switchMap((result) => this.uploadNewPicture().pipe(map(() => result))),
        switchMap((result) => this.saveCoverChange().pipe(map(() => result))),
      ).subscribe({
        next: (updatedPerimeters: KPI.Perimeter[]) => {
          this.loading = false;
          this.initialCoverId = this.coverId;
          this.perimetersService.perimetersHaveChanged();
          this.scopeService.refreshPerimeters();
          // Update initial values to track new state for future changes
          this.initialValues = {
            name: this.form.get('name')?.value ?? '',
            cluster: this.form.get('cluster')?.value ?? null,
          };
          // Update initial perimeters to track new state
          const perimeters = this.getPerimetersFormArray();
          this.initialPerimeters = perimeters.controls.map((control) => {
            const group = control as UntypedFormGroup;
            return {
              id: group.get('id')?.value || null,
              name: group.get('name')?.value || '',
              flooring_area: group.get('flooring_area')?.value || null,
              perimeter_type: group.get('perimeter_type')?.value || null,
              creation_year: group.get('creation_year')?.value || null,
            };
          });

          if (hasCreatedPerimeter) {
            const createdPerimeterIds = updatedPerimeters
              .map((perimeter) => perimeter?.id)
              .filter((id): id is number => id !== undefined && id !== null);
            const focusedPerimeterId = createdPerimeterIds[createdPerimeterIds.length - 1];
            const focusedPerimeter = updatedPerimeters.find((perimeter) => perimeter.id === focusedPerimeterId);

            this.saveToast.showSaved();
            this.nbDialogRef.close({ siteId: this.siteId, focusedPerimeterId, focusedPerimeter });
          } else {
            const updatedSubPerimeters = (this.site?.sub_perimeters || []).map(sp => {
              const control = this.getPerimetersFormArray().controls.find(c => c.value.id === sp.id);
              return (control && control.dirty) ? {
                ...sp,
                name: control.value.name,
                flooring_area: control.value.flooring_area,
                perimeter_type: control.value.perimeter_type,
                creation_year: control.value.creation_year,
              } : sp;
            });
            this.perimetersService.notifyPerimeterDataChanged(this.siteId, {
              name: this.form.get('name')?.value ?? this.site?.name,
              sub_perimeters: updatedSubPerimeters,
            });
            // Fixes #9270: give explicit confirmation on save, since the Update button
            // correctly re-disabling itself (nothing left to save) otherwise looks broken.
            this.saveToast.showSaved();
          }
        },
        error: () => {
          this.loading = false;
          this.saveToast.showError();
        },
      });
    } else {
      this._handleCreate();
    }
  }

  private _handleCreate() {
    this.loading = true;

    // Create new site with perimeters
    const formData = new FormData();
    formData.append('name', this.form.get('name').value);
    formData.append('cluster', this.form.get('cluster').value);

    // Add perimeters data to the FormData
    const perimeters = this.getPerimetersFormArray();
    for (let i = 0; i < perimeters.length; i++) {
      const perimeterGroup = perimeters.at(i) as UntypedFormGroup;
      formData.append(`perimeters[${i}][name]`, perimeterGroup.get('name').value);
      formData.append(`perimeters[${i}][perimeter_type]`, perimeterGroup.get('perimeter_type').value);
      const flooringArea = perimeterGroup.get('flooring_area').value;
      if (this.isBuildingPerimeter(i) && flooringArea !== null && flooringArea !== '') {
        formData.append(`perimeters[${i}][flooring_area]`, flooringArea);
      }
      const creationYear = perimeterGroup.get('creation_year').value;
      if (creationYear !== null && creationYear !== '') {
        formData.append(`perimeters[${i}][creation_year]`, creationYear);
      }
    }

    this.subscriptions.push(
      this.perimetersService.addMultiPerimeter(formData).pipe(
        switchMap((res) => {
          if (this.selectedPictureFile) {
            const picData = new FormData();
            picData.append('picture', this.selectedPictureFile);
            picData.append('perimeter', String(res.id));
            picData.append('order', '0');
            return this.api.structures_create_perimeter_picture(picData).pipe(
              map(() => res),
            );
          }
          return of(res);
        })
      ).subscribe((res) => {
        this.loading = false;
        this.perimetersService.perimetersHaveChanged();
        this.scopeService.refreshPerimeters();
        this.resetFileInput();
        this.createdSiteId = res.id;
        this.goToUserStep();
      }),
    );
  }

  private perimeterAtIndexChanged(group: UntypedFormGroup, initialPerimeter: any): boolean {
    const nameEq = (group.get('name')?.value ?? '') === (initialPerimeter.name ?? '');
    const floorEq = Number(group.get('flooring_area')?.value) === Number(initialPerimeter.flooring_area);
    const typeEq = Number(group.get('perimeter_type')?.value) === Number(initialPerimeter.perimeter_type);
    const yearEq = Number(group.get('creation_year')?.value) === Number(initialPerimeter.creation_year);
    return !(nameEq && floorEq && typeEq && yearEq);
  }

  public perimetersChanged(): boolean {
    const perimeters = this.getPerimetersFormArray();
    const initialLength = this.initialPerimeters?.length ?? 0;
    return perimeters.length !== initialLength ||
      (this.initialPerimeters && perimeters.controls.some((control, index) => {
        const group = control as UntypedFormGroup;
        const initialPerimeter = this.initialPerimeters[index];
        if (!initialPerimeter) {
          return true;
        }
        return this.perimeterAtIndexChanged(group, initialPerimeter);
      }));
  }

  public anyFieldValueChanged(): boolean {
    const anyFieldChanged = Object.keys(this.initialValues).some((key: string) =>
      this.fieldValueChanged(key),
    );
    return anyFieldChanged || this.perimetersChanged() || this.newPictureFiles.length > 0 || this.coverId !== this.initialCoverId;
  }

  public goToAudit(): void {}

  public isFormValid(): boolean {
    // Check if main form is valid and all perimeters have required data
    if (!this.form.valid) {
      return false;
    }

    const perimeters = this.getPerimetersFormArray();
    if (perimeters.length === 0) {
      return false;
    }

    // Check if all perimeters are valid
    for (let i = 0; i < perimeters.length; i++) {
      const perimeterGroup = perimeters.at(i) as UntypedFormGroup;
      if (!perimeterGroup.valid) {
        return false;
      }
    }

    return true;
  }

  public getPerimetersFormArray(): UntypedFormArray {
    return this.form.get('perimeters') as UntypedFormArray;
  }

  public isBuildingPerimeter(index: number): boolean {
    const perimeterTypeId = this.getPerimetersFormArray().at(index).get('perimeter_type')?.value;
    return this.isBuildingPerimeterType(perimeterTypeId);
  }

  private isBuildingPerimeterType(perimeterTypeId: number | null): boolean {
    const perimeterType = this.perimeterTypes.find(type => type.id === perimeterTypeId);
    return perimeterType?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING;
  }

  private getPerimeterValues(group: UntypedFormGroup): Partial<KPI.Perimeter> {
    const values = { ...group.value };
    if (!this.isBuildingPerimeterType(values.perimeter_type)) {
      delete values.flooring_area;
    }
    return values;
  }

  public createPerimeterFormGroup(data?: any): UntypedFormGroup {
    const flooringAreaValidators = [Validators.min(1)];
    const creationYearValidators = [Validators.min(1900), Validators.max(this.currentYear)];

    return this.formBuilder.group({
      id: new UntypedFormControl({ value: data?.id || null, disabled: false }),
      name: new UntypedFormControl(
        { value: data?.name || '', disabled: false },
        [Validators.required, Validators.maxLength(255)]
      ),
      flooring_area: new UntypedFormControl(
        { value: data?.flooring_area || null, disabled: false },
        flooringAreaValidators
      ),
      perimeter_type: new UntypedFormControl(
        { value: data?.perimeter_type || null, disabled: false },
        [Validators.required]
      ),
      creation_year: new UntypedFormControl(
        { value: data?.creation_year || null, disabled: false },
        creationYearValidators
      ),
    });
  }

  public addPerimeter(): void {
    const perimeters = this.getPerimetersFormArray();
    perimeters.push(this.createPerimeterFormGroup());
    perimeters.markAsTouched();
  }

  public removePerimeter(index: number): void {
    const perimeters = this.getPerimetersFormArray();
    if (perimeters.length <= 1) return;

    const group = perimeters.at(index) as UntypedFormGroup;
    const perimeterId = group.get('id')?.value;

    if (!perimeterId) {
      const confirmNewDialog = this.dialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('Confirmation'),
          content: this.translate.instant('Do you want to delete the selected building?'),
        },
        autoFocus: false,
      });
      confirmNewDialog.onClose.pipe(filter((confirmed) => !!confirmed)).subscribe(() => {
        perimeters.removeAt(index);
      });
      return;
    }

    this.loading = true;
    const subPerimeter = this.site?.sub_perimeters?.find((sp) => sp.id === perimeterId);
    const buildingIds = PerimetersService.getBuildingsIds(subPerimeter ? [subPerimeter] : []);
    this.perimetersService.hasDeletionBlockingObjects(perimeterId, buildingIds).subscribe({
      next: (hasLinked) => {
        this.loading = false;

        if (hasLinked) {
          this.dialogService.open(BasicDialogComponent, {
            context: {
              title: this.translate.instant('Perimeter deletion'),
              content: this.translate.instant(
                'Equipment sheets or investment sheets are attached to this perimeter, so it cannot be deleted in its current state. Please detach or delete these items first.'
              ),
              canCancel: false,
            },
            autoFocus: false,
          });
          return;
        }

        const confirmDialog = this.dialogService.open(BasicDialogComponent, {
          context: {
            title: this.translate.instant('Confirmation'),
            content: this.translate.instant('Do you want to delete the selected building?'),
          },
          autoFocus: false,
        });

        confirmDialog.onClose.pipe(filter((confirmed) => !!confirmed)).subscribe(() => {
          this.loading = true;
          this.perimetersService.deleteMonoPerimeter(perimeterId).subscribe({
            next: () => {
              this.perimetersService.perimetersHaveChanged();
              this.scopeService.refreshPerimeters();
              this.nbDialogRef.close({
                siteId: this.siteId,
                deletedPerimeterId: perimeterId,
              });
            },
            error: (_err) => {
              this.loading = false;
            },
          });
        });
      },
      error: (_err) => {
        this.loading = false;
      },
    });
  }

  public canRemovePerimeter(): boolean {
    return this.getPerimetersFormArray().length > 1;
  }

  protected setUpAvailableValues(): void {}

  public close(): void {
    // If closing from step 2, return created site ID so grid refreshes
    this.nbDialogRef.close(this.createdSiteId);
  }

  public resetFileInput() {
    const fileInput = document.getElementById('picture') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private loadPerimeterTypes(): void {
    const cluster = this.form?.get('cluster')?.value || (this.site ? this.site.cluster : null);
    this.subscriptions.push(
      this.scopeService.perimeterTypes$.pipe(
        idMapToArray(),
        map((types) => {
          return types.filter(type =>
            type.level === 1 && (cluster ? type.only_for_clusters.includes(cluster) : true)
          );
        })
      ).subscribe((filteredTypes) => {
        this.perimeterTypes = filteredTypes;
      })
    );
  }

  /**
   * Get cluster name by ID
   */
  public getClusterName(clusterId: number | null): string {
    if (!clusterId) return '';
    return this.clustersMap[clusterId] || '';
  }

  /**
   * Step 2: User selection for new sites
   */
  public goToUserStep(): void {
    this.currentStep = 2;
    this.loading = true;

    // Pre-select the current user (creator) by default and lock them
    this.subscriptions.push(
      this.scopeService.user$.pipe(first()).subscribe((currentUser) => {
        if (currentUser?.get_user_id) {
          this.currentUserId = currentUser.get_user_id;
          this.selectedUserIds.add(currentUser.get_user_id);
        }
      }),
    );

    this.subscriptions.push(
      this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_TEAM_MEMBER]).subscribe((hasPerm) => {
        this.hasEditPermission = hasPerm;
      }),
    );

    this.subscriptions.push(
      this.api.users_members(null, true).subscribe((users) => {
        this.allUsers = users;
        this.loading = false;
      }),
    );
  }

  public get filteredUsers(): KPI.User[] {
    if (!this.userSearchText) return this.allUsers;
    const search = this.userSearchText.toLowerCase();
    return this.allUsers.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
      return name.includes(search);
    });
  }

  public isUserSelected(user: KPI.User): boolean {
    return this.selectedUserIds.has(user.get_user_id);
  }

  public isCurrentUser(user: KPI.User): boolean {
    return user.get_user_id === this.currentUserId;
  }

  public areAllSelected(): boolean {
    return this.allUsers.length > 0 && this.allUsers.every(u => this.selectedUserIds.has(u.get_user_id));
  }

  public areSomeSelected(): boolean {
    return this.allUsers.some(u => this.selectedUserIds.has(u.get_user_id));
  }

  public toggleSelectAll(): void {
    if (!this.hasEditPermission) return;
    if (this.areAllSelected()) {
      // Deselect all except current user
      this.allUsers.forEach(u => {
        if (!this.isCurrentUser(u)) {
          this.selectedUserIds.delete(u.get_user_id);
        }
      });
    } else {
      // Select all
      this.allUsers.forEach(u => {
        this.selectedUserIds.add(u.get_user_id);
      });
    }
  }

  public toggleUserSelection(user: KPI.User): void {
    if (this.isCurrentUser(user) || !this.hasEditPermission) return;
    if (this.selectedUserIds.has(user.get_user_id)) {
      this.selectedUserIds.delete(user.get_user_id);
    } else {
      this.selectedUserIds.add(user.get_user_id);
    }
  }

  public getUserDisplayName(user: KPI.User): string {
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : user.email || `User ${user.id}`;
  }

  public saveWithUsers(): void {
    this.loading = true;
    this.saveToast.showSaving();
    // Exclude current user — backend already created their permission during site creation
    const userIds = Array.from(this.selectedUserIds).filter(id => id !== this.currentUserId);

    if (userIds.length === 0) {
      this.saveToast.showSaved();
      setTimeout(() => this.nbDialogRef.close(this.createdSiteId), 2000);
      return;
    }

    let completed = 0;
    userIds.forEach((userId) => {
      this.subscriptions.push(
        this.api.post_perimeter_permissions({
          perimeter: this.createdSiteId,
          user: userId,
        }).subscribe(() => {
          completed++;
          if (completed === userIds.length) {
            this.loading = false;
            this.saveToast.showSaved();
            setTimeout(() => this.nbDialogRef.close(this.createdSiteId), 2000);
          }
        }),
      );
    });
  }

}
